<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { Icons } from '@/models/icons.enum';
import { useAccountBalanceStore } from '@/stores/account-balance';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFormat } from '../../composables/format';

const { t } = useI18n();

type RegionalEmailConfig = {
  [key: string]: { email: string; subject: string };
};

export interface Props {
  selectedDocumentsTotal: number;
}

const props = withDefaults(defineProps<Props>(), {
  selectedDocumentsTotal: 0,
});

const { formatCurrency } = useFormat();
const { accountBalance, creditCardAnnualCap } = storeToRefs(useAccountBalanceStore());

const balanceDisplay = () => {
  if (!accountBalance.value?.currencyCode) {
    return '';
  }

  return formatCurrency(accountBalance.value?.currencyCode, accountBalance.value?.balance || 0, false);
};

const overdueDisplay = () => {
  if (!accountBalance.value?.currencyCode) {
    return '';
  }

  return formatCurrency(accountBalance.value?.currencyCode, accountBalance.value?.overdue || 0, false);
};

const isValidBalance = () => {
  return !isNaN(accountBalance.value?.balance || 0) && accountBalance.value?.currencyCode?.length !== 0;
};

const creditCardPaymentInfo = computed(() => {
  const paymentInfo = accountBalance.value?.creditCardPaymentInformation;
  const currencyCode = accountBalance.value?.currencyCode;

  if (!paymentInfo || !currencyCode || paymentInfo.restrictions.isFullyRestricted) {
    return {
      showLimitInfo: false,
      availableAmountRatio: 0,
      creditCardExpenditureWithCurrency: '',
      limitAmountWithCurrency: '',
      limitReached: false,
    };
  }

  const yearToDatePayments = paymentInfo.ytdPaymentsAmount ?? 0;

  const totalCreditCardExpenditure = yearToDatePayments + props.selectedDocumentsTotal;
  const totalCreditCardExpenditureWithCurrency = formatCurrency(
    accountBalance.value?.currencyCode,
    totalCreditCardExpenditure,
    false,
  );
  const limitAmount = creditCardAnnualCap.value;
  const limitAmountWithCurrency = formatCurrency(accountBalance.value?.currencyCode, limitAmount, false);

  return {
    showLimitInfo: true,
    availableAmountRatio: totalCreditCardExpenditure / limitAmount,
    totalCreditCardExpenditureWithCurrency,
    limitAmountWithCurrency,
    limitReached: totalCreditCardExpenditure >= limitAmount,
  };
});

const statementRequestEmail = (region: string) => {
  const emailConfig: RegionalEmailConfig = {
    eu: {
      email: t('viewAccounting.EUStatementRequestEmail'),
      subject: t('viewAccounting.EUStatementRequestSubject'),
    },
    'us-ca': {
      email: t('viewAccounting.USAndCanadaStatementRequestEmail'),
      subject: t('viewAccounting.USAndCanadaStatementRequestSubject'),
    },
  };
  let emailIdAndSubject = emailConfig[region] || emailConfig['us-ca'];

  return `mailto:${emailIdAndSubject.email}?subject=${emailIdAndSubject.subject}`;
};
</script>

<template>
  <div class="paper-container" data-cy="accounting-container">
    <div class="balance-container">
      <div class="text" data-cy="account-statement-text">
        <strong class="question">{{ $t('viewAccounting.accountBalanceQuestion') }}</strong>
        <div>
          {{ $t('viewAccounting.EURequest') }}
          <a target="_system" :href="statementRequestEmail('eu')">{{ $t('viewAccounting.here') }}</a>
        </div>
        <div>
          {{ $t('viewAccounting.USAndCanadaRequest') }}
          <a target="_system" :href="statementRequestEmail('us-ca')">{{ $t('viewAccounting.here') }}</a>
        </div>
      </div>
      <div class="amounts-container" data-cy="amounts-container">
        <div v-if="isValidBalance()" class="account-balance">
          <div
            v-if="creditCardPaymentInfo.showLimitInfo"
            class="amount-box balance"
            data-cy="account-balance-box-limit-info">
            <div class="amount-title" data-cy="account-balance-title">
              <label for="creditCardLimit" class="balance-label"> {{ $t('viewAccounting.credit_card_limit') }}</label>
              <span class="has-tooltip" data-cy="account-balance-tooltip">
                <BackstageIcon :name="Icons.TRANSPARENT_INFO_ICON" class="white-icon" />
                <span class="tooltip">
                  {{ $t('viewAccounting.credit_card_limit_tooltip') + creditCardPaymentInfo.limitAmountWithCurrency }}
                </span>
              </span>
            </div>
            <span
              class="balance-amount"
              :class="{ 'limit-reached': creditCardPaymentInfo.limitReached }"
              data-cy="account-balance-amount-limit-reached"
              >{{ creditCardPaymentInfo.totalCreditCardExpenditureWithCurrency }}</span
            >
            <div
              class="progress-bar"
              data-cy="account-balance-amount-progress-bar"
              :class="{ 'limit-reached': creditCardPaymentInfo.limitReached }"
              :style="{ '--progress': creditCardPaymentInfo.availableAmountRatio }"></div>
          </div>

          <div class="amount-box balance" data-cy="account-balance-box">
            <div class="amount-title" data-cy="account-balance-title">
              <label class="balance-label">{{ $t('viewAccounting.account_balance') }}</label>
              <span class="has-tooltip" data-cy="account-balance-tooltip">
                <BackstageIcon :name="Icons.TRANSPARENT_INFO_ICON" class="white-icon" />
                <span class="tooltip"> {{ $t('viewAccounting.account_balance_tootltip') }} </span>
              </span>
            </div>
            <span class="balance-amount" data-cy="account-balance-amount">{{ balanceDisplay() }}</span>
          </div>
          <div class="amount-box overdue" data-cy="balance-overdue-box">
            <div class="amount-title" data-cy="balance-overdue-title">
              <label class="balance-label"> {{ $t('viewAccounting.balance_overdue') }}</label>
              <span class="has-tooltip" data-cy="balance-overdue-tooltip">
                <BackstageIcon :name="Icons.TRANSPARENT_INFO_ICON" class="white-icon" />
                <span class="tooltip">
                  {{ $t('viewAccounting.balance_overdue_tootltip') }}
                </span>
              </span>
            </div>
            <span class="balance-amount" data-cy="balance-overdue-amount">{{ overdueDisplay() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.paper-container {
  margin-bottom: 2.25rem;
  padding: 1.625rem 1.25rem;
  width: 100%;
  background-color: var(--primary-bg-color);

  .balance-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;

    .text {
      text-align: left;
      display: flex;
      flex-direction: column;
      font-size: 0.875rem;

      .question {
        padding-bottom: 0.25rem;
        font-size: 1rem;
      }

      a {
        text-decoration: underline;
      }
    }

    .account-balance {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 20px;

      .amount-box {
        text-align: left;
        background-color: var(--bg-page-color);
        padding: 0.75rem;
        min-width: 230px;

        .balance-label {
          font-family: 'On Diatype Standard', Helvetica, sans-serif;
          font-weight: 500;
          font-size: 14px;
        }

        &.balance {
          .balance-amount {
            color: var(--primary-text-color);
          }
        }

        &.overdue {
          .balance-amount {
            color: var(--scarlet-red-color);
          }

          border-bottom: var(--scarlet-red-color) solid 1px;
        }

        .balance-amount {
          display: block;
          font-size: 14px;
          padding: 2px 0;
          font-family: 'On Diatype Standard', Helvetica, sans-serif;
          font-weight: 400;
        }

        .balance-amount.limit-reached {
          color: var(--scarlet-red-color);
        }

        .progress-bar {
          display: block;
          width: 100%;
          height: 4px;
          border-radius: 4px;
          background: linear-gradient(
            to right,
            var(--secondary-bg-color) calc(var(--progress) * 100%),
            var(--shade-3) 0
          );
        }

        .progress-bar.limit-reached {
          background: linear-gradient(
            to right,
            var(--scarlet-red-color) calc(var(--progress) * 100%),
            var(--primary-bg-color) 0
          );
        }
      }

      &.hidden {
        visibility: hidden;
      }
    }
  }

  .amount-title {
    display: flex;
    align-items: center;
    gap: 4px;

    .has-tooltip {
      @include breakpoint(medium) {
        display: block;
        margin: 5px 0 0;
        padding: 0;
        background-color: unset;
      }

      .white-icon {
        width: 18px;
        height: 18px;

        background-color: #fff;
        border-radius: 50%;

        display: flex;
        justify-content: center;
        align-items: center;
      }

      &:hover {
        .tooltip {
          display: block;
        }
      }

      position: relative;
      cursor: help;

      .tooltip {
        text-align: initial;
        color: var(--primary-text-color);
        position: absolute;
        bottom: 170%;
        right: 50%;
        width: max-content;
        min-width: 15em;
        max-width: 17em;
        z-index: 1;
        display: none;
        height: auto;
        transform: translateX(50%);
        padding: 18px 24px;
        box-shadow: 0 20px 40px -14px rgb(0 0 0 / 25%);
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
        background-color: var(--primary-bg-color);
      }
    }
  }
}
</style>
